import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { LearningModule, UserProgress, TaxonomyLevel } from '@/types/supabase';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, BookOpen, CheckCircle, Circle, Clock, Loader2 } from 'lucide-react';

const ModuleDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [module, setModule] = useState<LearningModule | null>(null);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();
  
  const lessons = [
    { id: 1, title: "Introduction to Prompted Agents", duration: "15 mins", completed: true },
    { id: 2, title: "Understanding AI Capabilities", duration: "25 mins", completed: true },
    { id: 3, title: "Basic Prompt Engineering", duration: "30 mins", completed: false },
    { id: 4, title: "Role Definition and Boundaries", duration: "20 mins", completed: false },
    { id: 5, title: "Testing and Iteration", duration: "35 mins", completed: false },
    { id: 6, title: "Module Assessment", duration: "45 mins", completed: false },
  ];

  useEffect(() => {
    const fetchModuleDetails = async () => {
      if (!id || !user) return;
      
      try {
        setIsLoading(true);
        
        const { data: moduleData, error: moduleError } = await supabase
          .from('learning_modules')
          .select('*')
          .eq('id', id)
          .single();
        
        if (moduleError) throw moduleError;
        
        setModule({
          ...moduleData,
          phase: moduleData.phase as LearningModule['phase'],
          track: moduleData.track as LearningModule['track'],
          taxonomy_level: moduleData.taxonomy_level as TaxonomyLevel[] || []
        });
        
        const { data: progressData, error: progressError } = await supabase
          .from('user_progress')
          .select('*')
          .eq('user_id', user.id)
          .eq('module_id', id)
          .single();
        
        if (!progressError) {
          setUserProgress(progressData);
        } else if (progressError.code !== 'PGRST116') {
          throw progressError;
        }
      } catch (error) {
        console.error('Error fetching module details:', error);
        toast({
          title: "Error loading module",
          description: "There was a problem loading the module details.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchModuleDetails();
  }, [id, user, toast]);

  const updateProgress = async (newPercentage: number) => {
    if (!user || !module || isUpdating) return;
    
    try {
      setIsUpdating(true);
      
      if (userProgress) {
        const { error } = await supabase
          .from('user_progress')
          .update({
            completion_percentage: newPercentage,
            completed_at: newPercentage === 100 ? new Date().toISOString() : null,
          })
          .eq('id', userProgress.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_progress')
          .insert({
            user_id: user.id,
            module_id: module.id,
            completion_percentage: newPercentage,
            started_at: new Date().toISOString(),
            completed_at: newPercentage === 100 ? new Date().toISOString() : null,
          });
        
        if (error) throw error;
        
        const { data, error: fetchError } = await supabase
          .from('user_progress')
          .select('*')
          .eq('user_id', user.id)
          .eq('module_id', module.id)
          .single();
        
        if (fetchError) throw fetchError;
        setUserProgress(data);
      }
      
      setUserProgress(prev => prev ? {
        ...prev,
        completion_percentage: newPercentage,
        completed_at: newPercentage === 100 ? new Date().toISOString() : null,
      } : null);
      
      toast({
        title: "Progress updated",
        description: newPercentage === 100 
          ? "Congratulations! You've completed this module." 
          : "Your progress has been saved.",
      });
    } catch (error) {
      console.error('Error updating progress:', error);
      toast({
        title: "Error updating progress",
        description: "There was a problem saving your progress.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCompleteLesson = (lessonId: number) => {
    const lessonIndex = lessons.findIndex(l => l.id === lessonId);
    if (lessonIndex === -1) return;
    
    const updatedLessons = [...lessons];
    updatedLessons[lessonIndex] = {
      ...updatedLessons[lessonIndex],
      completed: true
    };
    
    const completedCount = updatedLessons.filter(l => l.completed).length;
    const newPercentage = Math.round((completedCount / updatedLessons.length) * 100);
    
    updateProgress(newPercentage);
  };

  const getDifficultyColor = (level?: string) => {
    if (!level) return "";
    
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
      case 'intermediate':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100';
      case 'advanced':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!module) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold">Module Not Found</h2>
        <p className="text-muted-foreground mt-2">The module you're looking for doesn't exist.</p>
        <Button variant="outline" className="mt-6" asChild>
          <Link to="/modules">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Modules
          </Link>
        </Button>
      </div>
    );
  }

  const currentProgress = userProgress?.completion_percentage || 0;
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" className="p-0" asChild>
              <Link to="/modules">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Modules
              </Link>
            </Button>
          </div>
          <h1 className="text-2xl font-bold">{module.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={getDifficultyColor(module.difficulty_level)}>
              {module.difficulty_level}
            </Badge>
            {currentProgress > 0 && (
              <Badge variant="outline" className={currentProgress === 100 ? 'border-green-500 text-green-500' : 'border-blue-500 text-blue-500'}>
                {currentProgress === 100 ? 'Completed' : 'In Progress'}
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Module Overview</CardTitle>
              <CardDescription>
                {module.description || "Explore this module to enhance your skills"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-2">Progress</h3>
                  <div className="flex justify-between mb-1 text-sm">
                    <span>{currentProgress}% complete</span>
                    <span>{lessons.filter(l => l.completed).length}/{lessons.length} lessons</span>
                  </div>
                  <Progress value={currentProgress} className="h-2" />
                </div>
                
                <div className="pt-4">
                  <h3 className="text-sm font-medium mb-3">Module Description</h3>
                  <p className="text-muted-foreground">
                    {module.description || 
                      "This module will guide you through the fundamental concepts and practical applications of prompted agents, helping you develop the skills necessary to create effective AI assistants."}
                  </p>
                </div>
                
                <div className="pt-4">
                  <h3 className="text-sm font-medium mb-3">Learning Objectives</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 mr-2 mt-0.5 text-green-500" />
                      <span>Understand the core principles of prompted agents</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 mr-2 mt-0.5 text-green-500" />
                      <span>Learn effective prompt engineering techniques</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 mr-2 mt-0.5 text-green-500" />
                      <span>Create and test role-specific agent configurations</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 mr-2 mt-0.5 text-green-500" />
                      <span>Apply best practices for AI assistant development</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Module Content</CardTitle>
              <CardDescription>
                Complete all lessons to finish this module
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {lessons.map((lesson, index) => (
                  <div key={lesson.id} className="border rounded-lg p-4 transition-colors hover:bg-muted/50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start">
                        {lesson.completed ? (
                          <CheckCircle className="h-5 w-5 mr-3 mt-0.5 text-green-500 flex-shrink-0" />
                        ) : (
                          <Circle className="h-5 w-5 mr-3 mt-0.5 text-muted-foreground flex-shrink-0" />
                        )}
                        <div>
                          <h4 className="font-medium">
                            Lesson {index + 1}: {lesson.title}
                          </h4>
                          <div className="flex items-center mt-1 text-sm text-muted-foreground">
                            <Clock className="h-3.5 w-3.5 mr-1" />
                            <span>{lesson.duration}</span>
                          </div>
                        </div>
                      </div>
                      
                      <Button 
                        variant={lesson.completed ? "outline" : "default"} 
                        size="sm"
                        disabled={isUpdating || (index > 0 && !lessons[index - 1].completed)}
                        onClick={() => handleCompleteLesson(lesson.id)}
                      >
                        {isUpdating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : lesson.completed ? (
                          "Completed"
                        ) : (
                          "Mark as Complete"
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Resources</CardTitle>
              <CardDescription>
                Additional materials to support your learning
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                  <a href="#" className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-primary" />
                    <span className="text-sm">Module Handbook (PDF)</span>
                  </a>
                </div>
                
                <div className="border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                  <a href="#" className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-primary" />
                    <span className="text-sm">Prompt Engineering Guide</span>
                  </a>
                </div>
                
                <div className="border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                  <a href="#" className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-primary" />
                    <span className="text-sm">Practice Exercises</span>
                  </a>
                </div>
              </div>
              
              <Separator className="my-4" />
              
              <div>
                <h3 className="text-sm font-medium mb-3">Next Steps</h3>
                {currentProgress === 100 ? (
                  <div className="text-sm space-y-2">
                    <p className="flex items-center text-green-600">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Module completed!
                    </p>
                    <Button variant="outline" className="mt-2 w-full" asChild>
                      <Link to="/modules">
                        Continue to Next Module
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm space-y-2">
                    <p className="text-muted-foreground">Complete all lessons to finish this module.</p>
                    <p className="text-muted-foreground mt-1">
                      Current progress: <span className="font-medium">{currentProgress}%</span>
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ModuleDetail;
